import React, { useMemo, useState } from "react";
import { Box, Tooltip, Typography, ToggleButton, ToggleButtonGroup } from "@mui/material";
import { Product } from "../../../interfaces/DTO/mislaka_xml_models";
import { FundDataBasic } from "../../../interfaces/DTO/FundDataBasic";
import { MimshakService } from "../../../services/mimshakService";
import { formatCurrency } from "../../../utils/namesFormater";
import { getContinuousPercentileColor } from "../../../utils/fundsFormatter";

export type PerformancePeriod = "6m" | "12m";

export type RoutePerformance = {
  id: string;
  name: string;
  amount: number;
  percentile: number;
};

interface WeightedPerformanceBarProps {
  products: Product[];
  fundData?: FundDataBasic[];
}

const periodField: Record<PerformancePeriod, keyof FundDataBasic> = {
  "6m": "percentile_6m_yield",
  "12m": "percentile_12m_yield",
};

const periodLabel: Record<PerformancePeriod, string> = {
  "6m": "6M",
  "12m": "12M",
};

const buildRoutes = (
  products: Product[],
  fundData: FundDataBasic[] | undefined,
  period: PerformancePeriod
): RoutePerformance[] => {
  const routes: RoutePerformance[] = [];
  const field = periodField[period];

  products.forEach((product, productIndex) => {
    product.policies_or_accounts.forEach((policy, policyIndex) => {
      const mergedPaths = MimshakService.mergeDuplicateInvestmentPaths(policy.investment_paths);

      mergedPaths.forEach((path, pathIndex) => {
        const fund = fundData?.find((f) => f.fund_id === path.path_code);
        const percentile = fund?.[field] as number | undefined;
        const amount = path.accumulated_amount;

        if (percentile == null || isNaN(percentile) || !amount) {
          return;
        }

        routes.push({
          id: `${productIndex}-${policyIndex}-${pathIndex}`,
          name: fund?.fund_name || path.path_name || "מסלול השקעה",
          amount,
          percentile,
        });
      });
    });
  });

  return routes;
};

const WeightedPerformanceBar: React.FC<WeightedPerformanceBarProps> = ({ products, fundData }) => {
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const [period, setPeriod] = useState<PerformancePeriod>("12m");

  const routes = useMemo(() => buildRoutes(products, fundData, period), [products, fundData, period]);

  const totalAmount = useMemo(
    () => routes.reduce((sum, r) => sum + r.amount, 0),
    [routes]
  );

  const sortedRoutes = useMemo(
    () => [...routes].sort((a, b) => a.percentile - b.percentile),
    [routes]
  );

  const weightedPercentile = useMemo(() => {
    if (totalAmount === 0) return null;
    const sum = routes.reduce((acc, r) => acc + r.amount * r.percentile, 0);
    return sum / totalAmount;
  }, [routes, totalAmount]);

  const handlePeriodChange = (_event: React.MouseEvent<HTMLElement>, newPeriod: PerformancePeriod | null) => {
    if (newPeriod) {
      setPeriod(newPeriod);
    }
  };

  if (totalAmount === 0 || sortedRoutes.length === 0) {
    return null;
  }

  return (
    <Box sx={{ mb: 4 }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Box>
          <Typography variant="subtitle2" color="text.secondary" sx={{ display: "block", fontWeight: 700, letterSpacing: 0.3 }}>
            השוואת שוק
          </Typography>
          <Typography variant="h6" gutterBottom>
            איך מדורגת תשואת המסלולים שלכם ביחס למסלולים עם חשיפה דומה למניות?
          </Typography>
        </Box>
        <ToggleButtonGroup
          value={period}
          exclusive
          onChange={handlePeriodChange}
          size="small"
        >
          <ToggleButton value="6m">{periodLabel["6m"]}</ToggleButton>
          <ToggleButton value="12m">{periodLabel["12m"]}</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      <Box
        sx={{
          position: "relative",
          display: "flex",
          width: "100%",
          height: 40,
          borderRadius: "6px",
          overflow: "hidden",
          direction: "ltr",
        }}
      >
        {sortedRoutes.map((route) => {
          const widthPercent = (route.amount / totalAmount) * 100;
          const shareOfTotal = widthPercent;
          const color = getContinuousPercentileColor(route.percentile);
          const isHovered = hoveredId === route.id;

          return (
            <Tooltip
              key={route.id}
              title={
                <Box sx={{ whiteSpace: "pre-line", textAlign: "right" }}>
                  {`${route.name}\n${formatCurrency(route.amount)}\n${shareOfTotal.toFixed(1)}% מהנכסים המנותחים\nמיקום יחסי: ${Math.round(route.percentile)}`}
                </Box>
              }
            >
              <Box
                onMouseEnter={() => setHoveredId(route.id)}
                onMouseLeave={() => setHoveredId(null)}
                sx={{
                  width: `${widthPercent}%`,
                  height: "100%",
                  backgroundColor: color,
                  borderRight: "1px solid rgba(255,255,255,0.25)",
                  boxSizing: "border-box",
                  cursor: "pointer",
                  filter: isHovered ? "brightness(0.9)" : "none",
                  transition: "filter 0.15s ease",
                }}
              />
            </Tooltip>
          );
        })}

        <Box
          sx={{
            position: "absolute",
            top: 0,
            bottom: 0,
            left: "50%",
            width: "2px",
            backgroundColor: "rgba(0,0,0,0.55)",
            pointerEvents: "none",
          }}
        />
      </Box>

      <Box sx={{ position: "relative", height: 36 }}>
        <Box
          sx={{
            position: "absolute",
            left: "50%",
            transform: "translateX(-50%)",
            textAlign: "center",
            mt: 0.5,
          }}
        >
          <Typography variant="caption" sx={{ color: "text.secondary", display: "block" }}>
            מחצית מהכסף
          </Typography>
          <Typography variant="caption" sx={{ color: "text.secondary", display: "block" }}>
            {formatCurrency(totalAmount / 2)}
          </Typography>
        </Box>
      </Box>

      {weightedPercentile != null && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          מיקום יחסי משוקלל: {Math.round(weightedPercentile)}
        </Typography>
      )}
    </Box>
  );
};

export default WeightedPerformanceBar;
